import { useState } from "react";
import { StyleSheet, View } from "react-native";

import {
  AppModal,
  AppText,
  Button,
  ButtonText,
  SearchableSelect,
  Stack,
  TextField,
} from "~/components/ui";
import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import { ContributeTaxonForm } from "~/features/plants/ContributeTaxonForm";
import { TaxonSearchField } from "~/features/plants/TaxonSearchField";
import { usePlan } from "~/hooks/usePlan";
import { usePlantActions } from "~/state/plants";
import type { TaxonSearchResult } from "~/state/taxonomy";
import { spacing } from "~/styles/theme";
import type {
  PlantAcquisition,
  PlantPlacement,
  PlantStatus,
} from "~/api/types";

const statusOptions: { value: PlantStatus; label: string }[] = [
  { value: "thriving", label: "Thriving" },
  { value: "stable", label: "Doing fine" },
  { value: "struggling", label: "Struggling" },
  { value: "dormant", label: "Dormant" },
  { value: "dead", label: "Died" },
  { value: "gifted", label: "Gifted away" },
  { value: "sold", label: "Sold" },
];

const placementOptions: { value: PlantPlacement; label: string }[] = [
  { value: "pot", label: "In a pot" },
  { value: "ground", label: "In the ground" },
  { value: "raised_bed", label: "Raised bed" },
  { value: "hanging", label: "Hanging" },
  { value: "terrarium", label: "Terrarium" },
  { value: "hydroponic", label: "Hydroponic" },
  { value: "greenhouse", label: "Greenhouse" },
];

const acquisitionOptions: { value: PlantAcquisition; label: string }[] = [
  { value: "nursery", label: "Nursery" },
  { value: "garden_centre", label: "Garden centre" },
  { value: "gift", label: "Gift" },
  { value: "propagation", label: "Propagated" },
  { value: "seed", label: "Grown from seed" },
  { value: "trade", label: "Trade or swap" },
  { value: "wild_collected", label: "Wild collected" },
  { value: "inherited", label: "Inherited" },
  { value: "unknown", label: "Not sure" },
];

export function AddPlantModal({
  collectionId,
  visible,
  onClose,
  onCreated,
}: {
  collectionId: string;
  visible: boolean;
  onClose: () => void;
  onCreated?: (plantId: string) => void;
}) {
  const plan = usePlan();
  const plants = usePlantActions();
  const [taxon, setTaxon] = useState<TaxonSearchResult | null>(null);
  const [contributingTerm, setContributingTerm] = useState<string | null>(null);
  const [nickname, setNickname] = useState("");
  const [status, setStatus] = useState<PlantStatus>("stable");
  const [placement, setPlacement] = useState<PlantPlacement>("pot");
  const [acquisition, setAcquisition] = useState<PlantAcquisition>("unknown");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setTaxon(null);
    setContributingTerm(null);
    setNickname("");
    setStatus("stable");
    setPlacement("pot");
    setAcquisition("unknown");
    setNotes("");
    setError(null);
  }

  function close() {
    reset();
    onClose();
  }

  function submit() {
    if (!taxon && !nickname.trim()) {
      setError("Pick a plant from the index or give it a nickname.");
      return;
    }
    try {
      const plantId = plants.create({
        collectionId,
        taxonId: taxon?.id ?? null,
        nickname,
        fallbackName: taxon?.scientificName,
        status,
        placement,
        acquisition,
        notes,
      });
      reset();
      onCreated?.(plantId);
      onClose();
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "Unable to add this plant.",
      );
    }
  }

  return (
    <AppModal title="Add a plant" visible={visible} onClose={close}>
      {!plan.plants.allowed ? (
        <UpgradePrompt
          title="You've reached the free plant limit"
          reason={plan.plants.message ?? undefined}
        />
      ) : contributingTerm !== null ? (
        <ContributeTaxonForm
          initialName={contributingTerm}
          onCreated={(created) => {
            setTaxon(created);
            setContributingTerm(null);
          }}
          onCancel={() => setContributingTerm(null)}
        />
      ) : (
        <Stack gap="md">
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              What is it?
            </AppText>
            <TaxonSearchField
              selected={taxon}
              onSelect={setTaxon}
              onClear={() => setTaxon(null)}
              onRequestContribute={setContributingTerm}
            />
          </View>

          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Nickname (optional)
            </AppText>
            <TextField
              accessibilityLabel="Nickname"
              value={nickname}
              placeholder="The big one by the window"
              onChangeText={setNickname}
            />
          </View>

          <View style={stylesheet.grid}>
            <View style={stylesheet.gridField}>
              <AppText size="xs" weight="semibold">
                How's it doing?
              </AppText>
              <SearchableSelect
                title="Choose a status"
                accessibilityLabel="Status"
                value={status}
                options={statusOptions}
                placeholder="Doing fine"
                onValueChange={(value) => setStatus(value as PlantStatus)}
              />
            </View>
            <View style={stylesheet.gridField}>
              <AppText size="xs" weight="semibold">
                Where does it live?
              </AppText>
              <SearchableSelect
                title="Choose a placement"
                accessibilityLabel="Placement"
                value={placement}
                options={placementOptions}
                placeholder="In a pot"
                onValueChange={(value) => setPlacement(value as PlantPlacement)}
              />
            </View>
            <View style={stylesheet.gridField}>
              <AppText size="xs" weight="semibold">
                Where did it come from?
              </AppText>
              <SearchableSelect
                title="Choose a source"
                accessibilityLabel="Source"
                value={acquisition}
                options={acquisitionOptions}
                placeholder="Not sure"
                onValueChange={(value) =>
                  setAcquisition(value as PlantAcquisition)
                }
              />
            </View>
          </View>

          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Notes (optional)
            </AppText>
            <TextField
              accessibilityLabel="Notes"
              value={notes}
              placeholder="Repotted in spring, prefers morning sun"
              multiline
              style={stylesheet.textarea}
              onChangeText={setNotes}
            />
          </View>

          {error ? (
            <AppText size="xs" tone="danger">
              {error}
            </AppText>
          ) : null}

          {plan.plants.remaining !== null ? (
            <AppText size="xs">
              {plan.plants.remaining} free plant{" "}
              {plan.plants.remaining === 1 ? "slot" : "slots"} remaining.
            </AppText>
          ) : null}

          <Button iconName="plus" onPress={submit}>
            <ButtonText>Add plant</ButtonText>
          </Button>
        </Stack>
      )}
    </AppModal>
  );
}

const stylesheet = StyleSheet.create({
  field: { gap: spacing.xs },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.m,
  },
  gridField: { minWidth: 200, flex: 1, gap: spacing.xs },
  textarea: { minHeight: 86, textAlignVertical: "top" },
});
